import type { Tables } from "@msuite/utils/types";
import React from "react";
import { BlankSlate } from "../blank-slate";
import { Button } from "../button";
import { Lightbox } from "../lightbox";
import type { GetFileAnnotations, SaveFileAnnotations } from "../lightbox/shared";
import { useTranslation } from "../localization";
import { Modal, type ModalProps } from "../modal";
import type { UseRemoteFileUrl } from "../types";
import { FileInspectorModalItem } from "./file-inspector-modal-item";
import type { ItemFiles } from "./shared";

/** Props Interface */
interface FileInspectorModalProps<T extends { id: string }> extends ModalProps {
	isOpen: boolean;
	onClose: () => void;
	itemFiles: ItemFiles<T>;
	renderFile: (file: Tables<"files">) => React.ReactNode;
	useRemoteFileUrl?: UseRemoteFileUrl;
	getAnnotations?: GetFileAnnotations;
	onSaveAnnotations?: SaveFileAnnotations;
	annotatedFileIds?: Set<string>;
	bodyContent?: React.ReactNode;
	footerContent?: React.ReactNode;
}

export function FileInspectorModal<T extends { id: string }>({
	isOpen,
	onClose,
	itemFiles,
	renderFile,
	useRemoteFileUrl,
	getAnnotations,
	onSaveAnnotations,
	annotatedFileIds,
	bodyContent,
	footerContent,
}: FileInspectorModalProps<T>) {
	/** Hooks */
	const { t } = useTranslation();

	/** Render */
	return (
		<Modal
			title={t("files.files")}
			isOpen={isOpen}
			onClose={onClose}
		>
			<Modal.Body>
				{!itemFiles.length && <BlankSlate label={t("files.no_files_available")} />}
				<Lightbox.Provider itemHeight="0px">
					<Lightbox.Container>
						{itemFiles.map((item) => (
							<FileInspectorModalItem
								key={item.id}
								file={item.file}
								useRemoteFileUrl={useRemoteFileUrl}
								getAnnotations={getAnnotations}
								onSaveAnnotations={onSaveAnnotations}
								hasAnnotations={annotatedFileIds?.has(item.file.id)}
							>
								{renderFile(item.file)}
							</FileInspectorModalItem>
						))}
					</Lightbox.Container>
				</Lightbox.Provider>
				{bodyContent}
			</Modal.Body>
			<Modal.Footer>
				{footerContent}
				<div className="self-end h-stack gap-4 ml-auto">
					<Button
						variant="primary"
						onClick={onClose}
					>
						{t("general.done")}
					</Button>
				</div>
			</Modal.Footer>
		</Modal>
	);
}
